import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { MapPin, Clock, RefreshCw, Users, CheckCircle, XCircle, Navigation } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const GPSTrackingPage = () => {
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContractor, setSelectedContractor] = useState(null);
  const [locationHistory, setLocationHistory] = useState([]);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const fetchContractorLocations = useCallback(async () => {
    try {
      const response = await api.get('/gps/contractors');
      setContractors(response.data || []);
    } catch (error) {
      console.error('Failed to fetch GPS data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLocationHistory = async (contractorId) => {
    try {
      const response = await api.get(`/gps/history/${contractorId}`);
      setLocationHistory(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch location history');
    }
  };

  useEffect(() => {
    fetchContractorLocations();
  }, [fetchContractorLocations]);

  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchContractorLocations, 30000); // Refresh every 30 seconds
    }
    return () => clearInterval(interval);
  }, [autoRefresh, fetchContractorLocations]);

  const handleContractorSelect = (contractor) => {
    setSelectedContractor(contractor);
    if (contractor.user_id) {
      fetchLocationHistory(contractor.user_id);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString('en-AU', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusColor = (lastUpdate) => {
    if (!lastUpdate) return 'bg-gray-500';
    const diff = Date.now() - new Date(lastUpdate).getTime();
    if (diff < 300000) return 'bg-green-500'; // < 5 min
    if (diff < 1800000) return 'bg-yellow-500'; // < 30 min
    return 'bg-red-500'; // > 30 min
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="gps-tracking-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Navigation className="h-8 w-8 text-primary" />
            GPS Tracking
          </h1>
          <p className="text-muted-foreground mt-1">Track contractor locations in real-time</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={autoRefresh ? "default" : "outline"}
            onClick={() => setAutoRefresh(!autoRefresh)}
            size="sm"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
            {autoRefresh ? 'Auto-Refresh ON' : 'Auto-Refresh OFF'}
          </Button>
          <Button variant="outline" onClick={fetchContractorLocations} size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh Now
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Contractor List */}
        <Card className="bg-card border-border lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Contractors ({contractors.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : contractors.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                <MapPin className="h-12 w-12 mb-4 opacity-50" />
                <p>No contractor locations available</p>
                <p className="text-xs mt-2">Contractors need to check in via mobile app</p>
              </div>
            ) : (
              <div className="divide-y divide-border max-h-[500px] overflow-y-auto">
                {contractors.map((contractor) => (
                  <div
                    key={contractor.id}
                    className={`p-4 cursor-pointer hover:bg-secondary/50 transition-colors ${
                      selectedContractor?.id === contractor.id ? 'bg-secondary' : ''
                    }`}
                    onClick={() => handleContractorSelect(contractor)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{contractor.name}</p>
                        <p className="text-sm text-muted-foreground">{contractor.trade_type || 'General'}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${getStatusColor(contractor.last_location?.timestamp)}`} />
                        {contractor.is_checked_in ? (
                          <Badge variant="default" className="bg-green-500">On Site</Badge>
                        ) : (
                          <Badge variant="secondary">Offline</Badge>
                        )}
                      </div>
                    </div>
                    {contractor.last_location && (
                      <div className="mt-2 text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Last seen: {formatTime(contractor.last_location.timestamp)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Map View / Location Details */}
        <Card className="bg-card border-border lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              {selectedContractor ? `${selectedContractor.name}'s Location` : 'Location Map'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selectedContractor ? (
              <div className="space-y-4">
                {/* Current Location */}
                {selectedContractor.last_location ? (
                  <div className="p-4 bg-secondary/30 rounded-lg">
                    <h4 className="font-semibold mb-2">Current Location</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Latitude</p>
                        <p className="font-mono">{selectedContractor.last_location.latitude?.toFixed(6)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Longitude</p>
                        <p className="font-mono">{selectedContractor.last_location.longitude?.toFixed(6)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Accuracy</p>
                        <p>{selectedContractor.last_location.accuracy?.toFixed(0) || 'N/A'}m</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Last Update</p>
                        <p>{formatTime(selectedContractor.last_location.timestamp)}</p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3"
                      onClick={() => window.open(`https://www.google.com/maps?q=${selectedContractor.last_location.latitude},${selectedContractor.last_location.longitude}`, '_blank')}
                    >
                      <MapPin className="h-4 w-4 mr-2" />
                      Open in Google Maps
                    </Button>
                  </div>
                ) : (
                  <div className="p-4 bg-secondary/30 rounded-lg text-center text-muted-foreground">
                    <MapPin className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No location data available for this contractor</p>
                  </div>
                )}

                {/* Location History */}
                <div>
                  <h4 className="font-semibold mb-2">Location History</h4>
                  {locationHistory.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No history available</p>
                  ) : (
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {locationHistory.map((loc, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-secondary/20 rounded text-sm">
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-muted-foreground" />
                            <span className="font-mono text-xs">
                              {loc.latitude?.toFixed(4)}, {loc.longitude?.toFixed(4)}
                            </span>
                          </div>
                          <span className="text-muted-foreground text-xs">{formatTime(loc.timestamp)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                <MapPin className="h-16 w-16 mb-4 opacity-30" />
                <p>Select a contractor to view their location</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Total Contractors</p>
            <p className="text-2xl font-bold">{contractors.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Currently On Site</p>
            <p className="text-2xl font-bold text-green-500">
              {contractors.filter(c => c.is_checked_in).length}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Active (Last 30min)</p>
            <p className="text-2xl font-bold text-yellow-500">
              {contractors.filter(c => {
                if (!c.last_location?.timestamp) return false;
                return Date.now() - new Date(c.last_location.timestamp).getTime() < 1800000;
              }).length}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Offline</p>
            <p className="text-2xl font-bold text-muted-foreground">
              {contractors.filter(c => !c.is_checked_in).length}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GPSTrackingPage;
